/**
 * Desktop-side observability bootstrapping.
 *
 * Run once from `desktop/main.tsx` before React rendering. Installs consent gates:
 *   - Optional Sentry error reports with the dedicated desktop DSN.
 *   - Optional PostHog with the desktop surface property.
 *   - A local device identity only after explicit analytics consent.
 *
 * The DSN is hardcoded — Sentry DSNs are write-only public ingest
 * endpoints designed to ship in client bundles, so checking it in keeps
 * every developer / CI build / packaged .app pointing at the same
 * project with no env-var copying. See
 * https://docs.sentry.io/concepts/key-terms/dsn-explainer/.
 *
 * Override via `VITE_SENTRY_DSN_DESKTOP` (e.g. CI builds for a staging
 * Sentry project) — leave unset for the default.
 */
import { invoke } from "@tauri-apps/api/core";
import { initSentry } from "@/lib/sentry";
import {
  canLoadPostHog,
  initPostHog,
  onPostHogReady,
  posthog,
  registerSurface,
} from "@/lib/posthog";
import {
  analyticsAllowed,
  getPrivacyAccount,
  analyticsEpoch,
  subscribePrivacyConsent,
} from "@/lib/privacy-consent";
import { isTauri } from "@/lib/is-tauri";
import { telemetryEnvironment } from "@/lib/telemetry-context";
import { getWindowRoleSync } from "./use-window-role";

/**
 * Sentry project: `denker-dolcetto-desktop` (org `denker-ai`).
 * Used only for explicitly enabled webview error reports.
 */
const DEFAULT_DESKTOP_SENTRY_DSN =
  "https://4a9d1822d7fe58cccefe6ee7446b2f9e@o4510658306179072.ingest.de.sentry.io/4511315582320720";

let initialized = false;
const FIRST_OPEN_KEY = "denker.desktop.first_open_tracked";
let desktopIdentityReady: Promise<void> | null = null;
let desktopIdentityBootstrapped = false;

export async function initDesktopObservability(): Promise<void> {
  if (initialized) return;
  initialized = true;

  if (!isTauri()) {
    // Defensive: if this somehow runs in a non-Tauri context, no-op.
    return;
  }

  const dsn =
    (import.meta.env.VITE_SENTRY_DSN_DESKTOP as string | undefined) ||
    DEFAULT_DESKTOP_SENTRY_DSN;
  await initSentry({
    dsn,
    environment: telemetryEnvironment(),
  });

  initPostHog();
  registerSurface("desktop");

  let wasAllowed = false;
  const identifyWhenAllowed = () => {
    const allowed = analyticsAllowed();
    if (!allowed) {
      wasAllowed = false;
      desktopIdentityBootstrapped = false;
      desktopIdentityReady = null;
      return;
    }
    if (wasAllowed) return;
    wasAllowed = true;
    const identityEpoch = analyticsEpoch();
    const identityAccount = getPrivacyAccount();
    const current = () =>
      analyticsAllowed() &&
      analyticsEpoch() === identityEpoch &&
      getPrivacyAccount() === identityAccount;
    desktopIdentityReady = new Promise((resolve) => {
      onPostHogReady(() => {
        void (async () => {
          try {
            const deviceId = await invoke<string>("device_id");
            if (!current()) {
              resolve();
              return;
            }
            if (deviceId && identityAccount === null) {
              posthog.identify(deviceId);
              posthog.register({
                device_id: deviceId,
                os: detectOs(),
              });
            }
          } catch (e) {
            // Non-fatal — PostHog still works without device_id, just less
            // useful for pre-auth funnel attribution.
            console.warn("[observability] device_id fetch failed:", e);
          }

          if (!current()) {
            resolve();
            return;
          }
          desktopIdentityBootstrapped = true;
          resolve();

          // Every display owns a webview; only the owner represents an app open.
          if (getWindowRoleSync() !== "owner") return;
          const source = await installSource();
          if (!current()) return;
          const props = source ? { install_source: source } : {};
          // $set_once keeps the source of the first open this person reports.
          posthog.capture("desktop_app_opened", props, { $set_once: props });
          try {
            if (localStorage.getItem(FIRST_OPEN_KEY) !== "1") {
              localStorage.setItem(FIRST_OPEN_KEY, "1");
              posthog.capture("desktop_app_first_opened", props);
            }
          } catch {
            // Ignore storage failures; the per-launch event above still records usage.
          }
        })();
      });
    });
  };
  subscribePrivacyConsent(identifyWhenAllowed);
  identifyWhenAllowed();
}

/** Where this install came from (`install_source.rs`). */
type InstallSource = "microsoft_store" | "direct";

async function installSource(): Promise<InstallSource | undefined> {
  try {
    return await invoke<InstallSource>("install_source");
  } catch (e) {
    console.warn("[observability] install_source fetch failed:", e);
    return undefined;
  }
}

function detectOs(): "macos" | "windows" | "linux" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  const p = navigator.platform.toLowerCase();
  if (p.includes("mac")) return "macos";
  if (p.includes("win")) return "windows";
  if (p.includes("linux")) return "linux";
  return "unknown";
}

/**
 * Capture a desktop event. Use snake_case `desktop_<surface>_<verb>`
 * naming. Add new event names to `docs/reference/observability-events.md`
 * so dashboards have one source of truth.
 */
export function track(
  eventName: string,
  props?: Record<string, unknown>,
): void {
  if (!canLoadPostHog() || !analyticsAllowed()) return;
  const eventProps = props ?? {};
  const eventEpoch = analyticsEpoch();
  const eventAccount = getPrivacyAccount();
  const capture = () => {
    if (
      analyticsAllowed() &&
      analyticsEpoch() === eventEpoch &&
      getPrivacyAccount() === eventAccount
    ) {
      posthog.capture(eventName, eventProps);
    }
  };
  if (posthog.__loaded) {
    if (desktopIdentityReady && !desktopIdentityBootstrapped) {
      void desktopIdentityReady.then(capture);
    } else capture();
    return;
  }
  onPostHogReady(() => {
    void Promise.resolve(desktopIdentityReady).then(capture);
  });
}
